// Env-driven — no static fallback. Set NEXT_PUBLIC_SPORTS_EVENTS_API_URL
// in .env. An empty value here means any consumer that derives URLs
// from it will hit `/path` (relative), which is unlikely to be correct
// but at least fails loudly in the browser network panel rather than
// pointing at a stale localhost port.
export const SPORTS_EVENTS_API_BASE_URL =
  process.env.NEXT_PUBLIC_SPORTS_EVENTS_API_URL ?? '';

/**
 * Prefix for static images in public/ folder.
 * In deployed environments, CloudFront only routes sports/* to our server,
 * so images must be served from /sports/image/* instead of /image/*.
 * The rewrite in next.config.js maps /sports/image/* back to /image/* on the server.
 */
export const IMG_PREFIX = process.env.NODE_ENV === 'development' ? '' : '/sports';

type DateRangeKey = 'today' | 'next7days' | 'thisWeek' | 'thisMonth' | undefined;

interface DateFilterInput {
  dateRange?: string;
  dateFrom?: string;
  dateTo?: string;
}

/**
 * Build dateFrom/dateTo based on a high-level dateRange key plus any explicit dates.
 * - today / thisWeek / thisMonth override explicit dates
 * - anything else just passes through dateFrom/dateTo
 */
export function buildDateFilters(input: DateFilterInput): { dateFrom?: string; dateTo?: string } {
  const { dateRange, dateFrom, dateTo } = input;
  const key = (dateRange as DateRangeKey) || undefined;

  const format = (d: Date) =>
    new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(d);

  //const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
  const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

  if (!key) {
    // No predefined range – just pass through any explicit dates (used for custom range)
    return { dateFrom, dateTo };
  }

  const today = new Date();

  switch (key) {
    case 'today': {
      const value = format(today);
      return { dateFrom: value, dateTo: value };
    }
    case 'next7days':
    case 'thisWeek': {
      const from = format(today);
      const toDate = new Date(today);
      toDate.setDate(today.getDate() + 7);
      const to = format(toDate);
      return { dateFrom: from, dateTo: to };
    }
    case 'thisMonth': {
      const from = format(today);
      const to = format(endOfMonth(today));
      return { dateFrom: from, dateTo: to };
    }
    default:
      return { dateFrom, dateTo };
  }
}
